import { useContext, useEffect, useMemo } from "react";

import { useUser } from "@components/user-context";
import { makeUniversalBenchmarksAdoptableMetrics } from "@north-beam/nb-client-side";
import { getMetrics } from "@north-beam/nb-client-side/metrics-v2";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { UniversalBenchmarksContext } from "../context";
import { buildBenchmarkKey } from "../utils";

interface Args {
  level: string;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  breakdowns: Record<string, string>[] | null;
  filters: { breakdown: string; value: string }[];
}

export const useUniversalBenchmarksRow = ({
  level,
  attributionModel,
  attributionWindow,
  accountingMode,
  breakdowns,
  filters,
}: Args) => {
  const { user } = useUser();
  const context = useContext(UniversalBenchmarksContext);

  if (!context) {
    throw new Error(
      "useUniversalBenchmarksRow can only be used inside UniversalBenchmarksContext",
    );
  }

  const { adoptedBenchmark, benchmarkTargets, addQuery } = context;

  const adjustedAttributionWindow =
    accountingMode === "cash" ? "infinity" : attributionWindow;

  useEffect(() => {
    if (!breakdowns) {
      return;
    }

    addQuery({
      level,
      attributionModel,
      attributionWindow: adjustedAttributionWindow,
      accountingMode,
      breakdowns: breakdowns.flatMap((e) => Object.keys(e)),
      filters,
    });
  }, [
    level,
    attributionModel,
    adjustedAttributionWindow,
    accountingMode,
    breakdowns,
    filters,
  ]);

  return useMemo(() => {
    if (!breakdowns) {
      return null;
    }

    if (!adoptedBenchmark) {
      return null;
    }

    const benchmarkKey = buildBenchmarkKey(adoptedBenchmark.id, {
      level,
      attributionModel,
      attributionWindow: adjustedAttributionWindow,
      accountingMode,
      breakdowns: breakdowns.flatMap((e) => Object.keys(e)),
      filters,
    });

    const benchmarkTarget = benchmarkTargets.get(benchmarkKey);

    if (!benchmarkTarget) {
      return null;
    }

    const benchmarkMetrics = makeUniversalBenchmarksAdoptableMetrics(
      attributionModel,
      adjustedAttributionWindow,
      accountingMode,
      user as any,
    );

    const metricsV2 = getMetrics({
      user,
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity: "daily",
      customGoalRows: [],
      customMetricRows: [],
      showV1Forecasts: false,
    });

    const targets = benchmarkTarget.targets;

    if (!targets) {
      return {
        status: benchmarkTarget.status,
        adoptedMetricIds: adoptedBenchmark.selectedMetricIds,
        benchmarks: [],
      };
    }

    const targetKey =
      breakdowns.length === 0
        ? "topLevel"
        : breakdowns.flatMap((e) => Object.values(e)).join(" / ");

    const values = targets[targetKey];

    return {
      status: benchmarkTarget.status,
      adoptedMetricIds: adoptedBenchmark.selectedMetricIds,
      benchmarks: adoptedBenchmark.selectedMetricIds.map((metricId) => ({
        value: values?.[metricId],
        metric: benchmarkMetrics.metrics.find(
          (m) => m.metadata.baseId === metricId,
        ),
        metricV2: metricsV2.find((m) => m.id === metricId)!,
      })),
    };
  }, [
    level,
    attributionModel,
    adjustedAttributionWindow,
    accountingMode,
    breakdowns,
    filters,
    adoptedBenchmark,
    benchmarkTargets,
    user,
  ]);
};
